import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Check, ShieldCheck, Loader2, ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";
import { useAuth } from "../lib/auth";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const location = useLocation();

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)); }, []);

  useEffect(() => {
    if (location.state?.reason === "premium_required") {
      toast.error("This feature requires a Pro or Elite plan. Upgrade to continue.");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkout = async (plan) => {
    if (!user) { nav("/login"); return; }
    if (!plan.lookup_key) return;
    setBusy(plan.lookup_key);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load Razorpay. Check your connection."); return; }

      const { data } = await api.post("/payments/create-order", { lookup_key: plan.lookup_key });

      await new Promise((resolve, reject) => {
        const rzp = new window.Razorpay({
          key: data.key_id,
          amount: data.amount,
          currency: data.currency,
          name: "Project Wingman",
          description: `${plan.name} Plan`,
          order_id: data.order_id,
          prefill: {
            email: user.email,
            name: user.name,
            method: "upi",
          },
          theme: { color: "#D3FF24" },
          handler: async (response) => {
            try {
              await api.post("/payments/verify", {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                lookup_key: plan.lookup_key,
              });
              toast.success(`${plan.name} plan activated!`);
              if (refreshUser) await refreshUser();
              nav("/payment/success");
            } catch (e) {
              toast.error("Payment verification failed. Contact support.");
              reject(e);
            }
            resolve();
          },
          modal: { ondismiss: () => { resolve(); } },
        });
        rzp.open();
      });
    } catch (e) {
      if (e?.response?.data?.detail) toast.error(e.response.data.detail);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <SEO
        title="Pricing — Free, Pro & Elite Plans"
        description="Start free with 10 minutes of live coaching. Upgrade to Pro for ₹799 per interview or Elite for ₹4,999/month with unlimited interviews and priority AI models."
        path="/pricing"
      />
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/wingman-logo.png" alt="Wingman.AI" className="h-10 w-auto" />
            <span className="font-display font-bold tracking-tight text-lg">WINGMAN<span className="text-[#D3FF24]">.</span>AI</span>
          </Link>
          {user ? (
            <Link to="/app" className="text-sm text-neutral-300 hover:text-white inline-flex items-center gap-1">
              <ArrowLeft className="w-3.5 h-3.5" />Dashboard
            </Link>
          ) : (
            <Link to="/login" className="text-sm text-neutral-300 hover:text-white">Sign in</Link>
          )}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-3">/ Pricing</div>
          <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter mb-4">
            Simple, <span className="text-premium">honest</span> pricing.
          </h1>
          <p className="text-neutral-400 max-w-xl mx-auto">Start free. Upgrade when you're serious. Cancel anytime.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10" data-testid="pricing-grid">
          {plans.map((p) => {
            const isPro = p.id === "pro";
            const isCurrent = user?.plan === p.id;
            return (
              <div key={p.id} className={`bg-[#0A0A0A] p-8 flex flex-col ${isPro ? "relative" : ""}`}>
                {isPro && (
                  <div className="absolute top-0 right-0 bg-[#D3FF24] text-black text-[10px] uppercase tracking-widest px-3 py-1 font-semibold">Popular</div>
                )}
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">{p.name}</div>
                <div className="font-display flex items-baseline gap-1 mb-6">
                  <span className="text-3xl font-black tracking-tighter text-neutral-400">₹</span>
                  <span className="text-5xl font-black tracking-tighter">{p.price_monthly}</span>
                  <span className="text-sm text-neutral-500">{p.id === "elite" ? " /month" : p.id === "pro" ? " /interview" : ""}</span>
                  {p.price_monthly > 0 && <span className="text-[10px] text-neutral-600 ml-1">+ taxes</span>}
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-neutral-300">
                      <Check className="w-4 h-4 text-[#D3FF24] mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {p.id === "free" ? (
                  <Link
                    to={user ? "/app" : "/register"}
                    data-testid={`plan-${p.id}-cta`}
                    className="block text-center border border-white/15 py-3 text-xs uppercase tracking-widest font-semibold hover:bg-white/5 transition">
                    {user ? "Current plan" : "Get started"}
                  </Link>
                ) : (
                  <button
                    data-testid={`plan-${p.id}-cta`}
                    disabled={busy === p.lookup_key || isCurrent}
                    onClick={() => checkout(p)}
                    className={`py-3 text-xs uppercase tracking-widest font-semibold transition disabled:opacity-60 ${
                      isPro
                        ? "bg-premium text-white hover:brightness-110 glow-blue"
                        : "bg-[#D3FF24] text-black hover:brightness-105"
                    } flex items-center justify-center gap-2`}>
                    {busy === p.lookup_key && <Loader2 className="w-4 h-4 animate-spin" />}
                    {isCurrent ? "Current plan" : `Upgrade to ${p.name}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-neutral-600 mt-12">
          Payments powered by Razorpay · UPI · GPay · PhonePe · Cards · Netbanking
        </p>
      </div>
    </div>
  );
}
